import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { ForgotPasswordRequest } from '../../../../../models/users/requests/forgot-password-request';
import { PortalService } from '../../../../../services/portal/portal.service';
import { ForgotPasswordViewModel } from './forgot-password-view-model';
import { ButtonTypeEnum } from '../../../../../ui/buttons/button/button-type.enum';
import { EmailInputComponent } from '../../../../../ui/form-components/email-input/email-input/email-input.component';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  providers: [ForgotPasswordViewModel],
  styleUrls: ['./forgot-password.component.scss', '../auth.component.scss']
})
export class ForgotPasswordComponent implements AfterViewInit {
  private viewModel = inject(ForgotPasswordViewModel);
  private portalService = inject(PortalService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  @ViewChild('emailInput', { static: true }) emailInput!: EmailInputComponent;

  private listenToLoadingOpts = this.viewModel.loadingOpts$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(opts => this.loadingOpts.emit(opts));

  public readonly emailSent$ = this.viewModel.emailSent$;
  public readonly requestFailed$ = this.viewModel.requestFailed$;
  public readonly isMember$ = this.portalService.isMember$;
  public readonly resetPasswordText = $localize`Reset Password`;
  public readonly emailSentText = $localize`Reset Password Link Sent`;

  public formSubmitted = false;

  public formGroup = new FormGroup({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    })
  });

  public customErrorMessages = {
    email: {
      required: $localize`Email is required`,
      email: $localize`Invalid email address`
    }
  };

  public ngAfterViewInit(): void {
    this.emailInput.input.nativeElement.focus();
  }

  private listenToPreFillEmail = this.viewModel.preFillEmail$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(email => this.formGroup.controls.email.setValue(email));

  private clearFailedStateOnEdit = this.formGroup.controls.email.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => this.viewModel.clearFailedState());

  public resetPassword(): void {
    this.formSubmitted = true;
    if (this.formGroup.valid) {
      const { email } = this.formGroup.value;
      const req = new ForgotPasswordRequest(email);
      this.portalService.portalType$.once(type => this.viewModel.forgotPassword(req, type));
    }
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
